import React from "react";
import { Box, Typography, useTheme } from "@mui/material";

// Иконки лежат в public/crypto-icons/. Ключ — (currency, network).
// Если конкретной комбинации нет — падаем на generic currency-иконку.
const CURRENCY_ICON: Record<string, string> = {
  BTC: "/crypto-icons/btc.png",
  LTC: "/crypto-icons/ltc.png",
  TRX: "/crypto-icons/trx.png",
  BNB: "/crypto-icons/bnb.png",
  USDT: "/crypto-icons/usdt.png",
  USDC: "/crypto-icons/usdc.png",
  ETH: "/crypto-icons/eth.png",
};

const NETWORK_VARIANT: Record<string, string> = {
  "USDT:TRC20": "/crypto-icons/usdt-trc20.png",
  "USDT:BEP20": "/crypto-icons/usdt-bep20.png",
  "USDC:TRC20": "/crypto-icons/usdc-trc20.png",
  "USDC:BEP20": "/crypto-icons/usdc-bep20.png",
};

const NETWORK_SHORT: Record<string, string> = {
  BITCOIN: "BTC",
  LITECOIN: "LTC",
  TRON: "TRC20",
  TRC20: "TRC20",
  BEP20: "BEP20",
  BSC: "BEP20",
  ERC20: "ERC20",
};

interface Props {
  currency: string;
  network: string;
  size?: number;
}

/**
 * Компактный бейдж «валюта + сеть».
 * Показывает иконку монеты и label сети (мелким текстом снизу),
 * либо специальный вариант иконки для комбинации (USDT-TRC20 и т.п.).
 */
export const CryptoBadge: React.FC<Props> = ({ currency, network, size = 28 }) => {
  const theme = useTheme();
  const variantKey = `${currency}:${network}`;
  const iconSrc =
    NETWORK_VARIANT[variantKey] ?? CURRENCY_ICON[currency] ?? "";
  const shortNet = NETWORK_SHORT[network] ?? network;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
      {iconSrc ? (
        <Box
          component="img"
          src={iconSrc}
          alt={currency}
          sx={{
            width: size,
            height: size,
            borderRadius: "50%",
            objectFit: "cover",
            boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
          }}
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = "none";
          }}
        />
      ) : (
        <Box
          sx={{
            width: size,
            height: size,
            borderRadius: "50%",
            background:
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.08)"
                : "rgba(0,0,0,0.06)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 11,
            fontWeight: 700,
          }}
        >
          {currency.slice(0, 2)}
        </Box>
      )}
      <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1.1 }}>
        <Typography
          sx={{
            fontSize: "20px",
            fontWeight: 500,
            color: "rgba(140, 140, 141, 1)",
          }}
        >
          {currency}
        </Typography>
        <Typography
          sx={{
            fontSize: "10px",
            fontWeight: 500,
            color: "rgba(140, 140, 141, 0.75)",
          }}
        >
          {shortNet}
        </Typography>
      </Box>
    </Box>
  );
};
